// Text eines Kommentars: Auszeichnung (**fett**, *kursiv*, {farbe:Text}) und Erwähnungen (@Name, @E-Mail, @Alle, @Firmenname).
// Gespeichert wird der Text mit Auszeichnung; der Server erkennt Erwähnungen genauso (Benachrichtigungen.Erwaehnte)
// und entfernt die Auszeichnung für Benachrichtigungen und die Suche (Kommentare.Klartext).
import type { Person } from './api';

export const FARBEN = [
  { id: 'rot', name: 'Rot' },
  { id: 'orange', name: 'Orange' },
  { id: 'gruen', name: 'Grün' },
  { id: 'blau', name: 'Blau' },
  { id: 'violett', name: 'Violett' },
  { id: 'grau', name: 'Grau' },
] as const;
export type Farbe = (typeof FARBEN)[number]['id'];

export type Knoten =
  | string
  | { art: 'fett' | 'kursiv'; kinder: Knoten[] }
  | { art: 'farbe'; farbe: Farbe; kinder: Knoten[] }
  /** gruppe gesetzt: @Alle bzw. @Firmenname (dann ist email leer) */
  | { art: 'erwaehnung'; email: string; text: string; gruppe?: string };

const FARBE_AUF = new RegExp(`^\\{(${FARBEN.map((f) => f.id).join('|')}):`);
const leer = (z: string | undefined) => z === undefined || /\s/.test(z);
const buchstabe = (z: string | undefined) => z !== undefined && /[\p{L}\p{N}]/u.test(z);

/** Ende eines mit `*` begonnenen kursiven Abschnitts (fette Abschnitte darin werden übersprungen); -1 = keines. */
function kursivEnde(s: string, ab: number): number {
  for (let k = ab; k < s.length; k++) {
    if (s[k] !== '*') continue;
    if (s[k + 1] === '*') {
      const zu = s.indexOf('**', k + 2);
      if (zu < 0) return -1;
      k = zu + 1;
    } else return k;
  }
  return -1;
}

/** Schließende Klammer zu einer `{farbe:`-Klammer (geschachtelte zählen mit); -1 = keine. */
function klammerEnde(s: string, ab: number): number {
  let tiefe = 0;
  for (let k = ab; k < s.length; k++) {
    if (s[k] === '{') tiefe++;
    else if (s[k] === '}' && tiefe-- === 0) return k;
  }
  return -1;
}

function auszeichnung(s: string): Knoten[] {
  const l: Knoten[] = [];
  let text = '';
  const dazu = (k: Knoten) => { if (text) { l.push(text); text = ''; } l.push(k); };
  for (let i = 0; i < s.length;) {
    if (s.startsWith('**', i) && !leer(s[i + 2])) {
      let zu = s.indexOf('**', i + 3);
      while (zu >= 0 && s[zu + 2] === '*') zu++;
      if (zu >= 0 && !leer(s[zu - 1])) { dazu({ art: 'fett', kinder: auszeichnung(s.slice(i + 2, zu)) }); i = zu + 2; continue; }
    } else if (s[i] === '*' && s[i + 1] !== '*' && !leer(s[i + 1])) {
      const zu = kursivEnde(s, i + 1);
      if (zu > i + 1 && !leer(s[zu - 1])) { dazu({ art: 'kursiv', kinder: auszeichnung(s.slice(i + 1, zu)) }); i = zu + 1; continue; }
    } else if (s[i] === '{') {
      const m = FARBE_AUF.exec(s.slice(i, i + 12));
      const zu = m ? klammerEnde(s, i + m[0].length) : -1;
      if (m && zu > i + m[0].length) { dazu({ art: 'farbe', farbe: m[1] as Farbe, kinder: auszeichnung(s.slice(i + m[0].length, zu)) }); i = zu + 1; continue; }
    }
    text += s[i++];
  }
  if (text) l.push(text);
  return l;
}

/** Name, mit dem eine Person per @ angesprochen werden kann (wie auf dem Server: mindestens 2 Zeichen, kein @). */
const rufname = (p: Person) => {
  const n = (p.name ?? '').trim();
  return n.length >= 2 && !n.includes('@') ? n : null;
};

/** Erwähnung aller Mitglieder des Projekts (wie Firmen.Alle auf dem Server). */
export const ALLE = 'Alle';

/** Gruppen, die sich per @ ansprechen lassen: „Alle“ und die Firmen der Personen (aus der E-Mail-Domäne, sortiert). */
export function gruppen(personen: Person[]): string[] {
  const firmen = new Map<string, string>();
  for (const p of personen) if (p.firma && !firmen.has(p.firma.toLowerCase())) firmen.set(p.firma.toLowerCase(), p.firma);
  return personen.length === 0 ? [] : [ALLE, ...[...firmen.values()].sort((a, b) => a.localeCompare(b))];
}

/** Personen einer Gruppe: alle bzw. die Mitglieder der Firma. */
export const inGruppe = (personen: Person[], gruppe: string) =>
  personen.filter((p) => p.email.includes('@') && (gruppe === ALLE || p.firma?.toLowerCase() === gruppe.toLowerCase()));

/** Erwähnungen in einem Textstück (Grenzen wie Benachrichtigungen.Kommt auf dem Server; die längste Angabe gewinnt). */
function erwaehnungen(s: string, personen: Person[]): Knoten[] {
  if (!s.includes('@') || personen.length === 0) return [s];
  const muster: { muster: string; email: string; gruppe?: string }[] = [
    ...personen.flatMap((p) => [p.email, rufname(p)].filter((m): m is string => !!m).map((m) => ({ muster: '@' + m.toLowerCase(), email: p.email }))),
    ...gruppen(personen).map((g) => ({ muster: '@' + g.toLowerCase(), email: '', gruppe: g })),
  ].sort((a, b) => b.muster.length - a.muster.length);
  const klein = s.toLowerCase();
  const l: Knoten[] = [];
  let ab = 0;
  for (let i = s.indexOf('@'); i >= 0; i = s.indexOf('@', i + 1)) {
    if (i < ab || (i > 0 && (buchstabe(s[i - 1]) || '._-'.includes(s[i - 1])))) continue;
    const treffer = muster.find((m) => {
      if (!klein.startsWith(m.muster, i)) return false;
      const e = i + m.muster.length;
      return !(buchstabe(s[e]) || (e < s.length && '_-@'.includes(s[e])) || (s[e] === '.' && buchstabe(s[e + 1])));
    });
    if (!treffer) continue;
    if (i > ab) l.push(s.slice(ab, i));
    ab = i + treffer.muster.length;
    l.push({ art: 'erwaehnung', email: treffer.email, text: s.slice(i, ab), ...(treffer.gruppe ? { gruppe: treffer.gruppe } : {}) });
  }
  if (ab < s.length) l.push(s.slice(ab));
  return l;
}

const mitErwaehnungen = (l: Knoten[], personen: Person[]): Knoten[] =>
  l.flatMap((k) => typeof k === 'string' ? erwaehnungen(k, personen)
    : k.art === 'erwaehnung' ? [k] : [{ ...k, kinder: mitErwaehnungen(k.kinder, personen) }]);

/** Kommentartext in Abschnitte zerlegen: Auszeichnung und – mit Personenliste – Erwähnungen. */
export function zerlegen(text: string, personen: Person[] = []): Knoten[] {
  return mitErwaehnungen(auszeichnung(text), personen);
}

/** Text ohne Auszeichnungszeichen (zum Kopieren). */
export function klartext(text: string): string {
  const flach = (l: Knoten[]): string => l.map((k) => typeof k === 'string' ? k : k.art === 'erwaehnung' ? k.text : flach(k.kinder)).join('');
  return flach(auszeichnung(text));
}

export interface Eingabe { text: string; start: number; ende: number }

/**
 * Markierung mit `vor`/`nach` umschließen; ist sie schon so umschlossen, die Zeichen wieder entfernen.
 * Ohne Markierung entsteht ein leeres Paar mit der Schreibmarke dazwischen. Leerraum am Rand bleibt außerhalb.
 */
export function umschliessen({ text, start, ende }: Eingabe, vor: string, nach: string): Eingabe {
  const mitte = text.slice(start, ende);
  start += mitte.length - mitte.trimStart().length;
  ende -= mitte.length - mitte.trimEnd().length;
  if (ende < start) ende = start;
  if (text.slice(start - vor.length, start) === vor && text.slice(ende, ende + nach.length) === nach)
    return { text: text.slice(0, start - vor.length) + text.slice(start, ende) + text.slice(ende + nach.length), start: start - vor.length, ende: ende - vor.length };
  return { text: text.slice(0, start) + vor + text.slice(start, ende) + nach + text.slice(ende), start: start + vor.length, ende: ende + vor.length };
}

/**
 * Zeilen der Markierung mit einem Vorsatz versehen („- “ für Aufzählung, „### “ für Überschrift); tragen ihn schon alle
 * nicht leeren Zeilen, wird er entfernt. Ohne Markierung gilt die Zeile der Schreibmarke.
 */
export function zeilenVorsatz({ text, start, ende }: Eingabe, vorsatz: string): Eingabe {
  const von = text.lastIndexOf('\n', start - 1) + 1;
  const bisRoh = text.indexOf('\n', Math.max(ende, start));
  const bis = bisRoh < 0 ? text.length : bisRoh;
  const zeilen = text.slice(von, bis).split('\n');
  const belegt = zeilen.filter((z) => z.trim());
  const weg = belegt.length > 0 && belegt.every((z) => z.startsWith(vorsatz));
  const neu = zeilen.map((z) => (weg ? (z.startsWith(vorsatz) ? z.slice(vorsatz.length) : z) : z.trim() || zeilen.length === 1 ? vorsatz + z : z)).join('\n');
  return { text: text.slice(0, von) + neu + text.slice(bis), start: von, ende: von + neu.length };
}

/** Markierung einfärben; eine schon vorhandene Farbe direkt um die Markierung wird ersetzt (dieselbe Farbe: entfernt). */
export function faerben(e: Eingabe, farbe: Farbe): Eingabe {
  const davor = /\{([a-z]+):$/.exec(e.text.slice(0, e.start));
  if (davor && FARBEN.some((f) => f.id === davor[1]) && e.text[e.ende] === '}') {
    const ohne = umschliessen(e, davor[0], '}');
    return davor[1] === farbe ? ohne : umschliessen(ohne, `{${farbe}:`, '}');
  }
  return umschliessen(e, `{${farbe}:`, '}');
}

/** Angefangene Erwähnung vor der Schreibmarke: Stelle des @ und der bisher getippte Suchtext; null = keine. */
export function erwaehnungAn(text: string, marke: number): { start: number; suche: string } | null {
  for (let i = marke - 1; i >= 0 && marke - i <= 60 && text[i] !== '\n'; i--) {
    if (text[i] !== '@' || (i > 0 && (buchstabe(text[i - 1]) || '._-'.includes(text[i - 1])))) continue;
    return { start: i, suche: text.slice(i + 1, marke) };
  }
  return null;
}

/** Personen, die zum getippten Suchtext passen: Anfang eines Namensteils oder der E-Mail-Adresse. */
export function erwaehnbare(personen: Person[], suche: string, ohne?: string | null): Person[] {
  const q = suche.toLowerCase();
  return personen.filter((p) => p.email.includes('@') && p.email !== ohne
    && (p.email.toLowerCase().startsWith(q) || (' ' + (p.name ?? '').toLowerCase()).includes(' ' + q)));
}

/**
 * Angabe hinter dem @: der Name, wenn er die Person eindeutig trifft – sonst die E-Mail-Adresse
 * (z. B. „Anna“ neben „Anna Muster“: der Server würde bei „@Anna Muster“ sonst beide benachrichtigen).
 */
export function erwaehnungText(person: Person, personen: Person[]): string {
  const n = rufname(person)?.toLowerCase();
  const eindeutig = n && !personen.some((p) => p.email !== person.email && (() => {
    const m = rufname(p)?.toLowerCase();
    const grenze = (lang: string, kurz: string) => lang.startsWith(kurz) && !(buchstabe(lang[kurz.length]) || '_-@'.includes(lang[kurz.length] ?? ' '));
    return !!m && (grenze(m, n) || grenze(n, m));
  })());
  return '@' + (eindeutig ? rufname(person) : person.email);
}

/** Gruppen, die zum getippten Suchtext passen (Anfang eines Wortes im Namen). */
export function erwaehnbareGruppen(personen: Person[], suche: string): string[] {
  const q = suche.toLowerCase();
  return gruppen(personen).filter((g) => (' ' + g.toLowerCase()).includes(' ' + q));
}

/** Die angefangene Erwähnung (ab `start` bis zur Schreibmarke) durch die gewählte Person bzw. Gruppe (Name als Text) ersetzen. */
export function erwaehnungEinsetzen(text: string, start: number, marke: number, person: Person | string, personen: Person[]): Eingabe {
  const neu = (typeof person === 'string' ? '@' + person : erwaehnungText(person, personen)) + (text[marke] === ' ' ? '' : ' ');
  const pos = start + neu.length + (text[marke] === ' ' ? 1 : 0);
  return { text: text.slice(0, start) + neu + text.slice(marke), start: pos, ende: pos };
}
